import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Search, FileEdit, X, RotateCcw, ArrowLeft, ExternalLink, ShieldAlert } from "lucide-react";
import api from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { timeAgo, fullDate } from "@/lib/format";
import { draftPolicy, readinessMeta } from "@/lib/readiness";

const asArr = (x) => (Array.isArray(x) ? x : x === null || x === undefined || x === "" ? [] : [x]);

const PRIORITY_COLOR = {
  "Breaking": "text-crimson border-crimson", "High Priority": "text-amber border-amber",
  "Normal": "text-cyan border-cyan", "Low Priority": "text-admmuted border-admborder",
};
const STATUS_BADGE = {
  new: "bg-cyan/15 text-cyan", researched: "bg-amber/15 text-amber",
  drafted: "bg-emerald/15 text-emerald", ignored: "bg-admmuted/15 text-admmuted",
};

export default function CandidateDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const [c, setC] = useState(null);
  const [researching, setResearching] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [overrideDNP, setOverrideDNP] = useState(false);
  const poll = useRef(null);
  const autoStarted = useRef(false);

  const fetchC = useCallback(async () => {
    try { const { data } = await api.get(`/agent/candidates/${id}`); setC(data); return data; }
    catch { toast.error("Candidate not found"); return null; }
  }, [id]);

  useEffect(() => { fetchC(); return () => poll.current && clearInterval(poll.current); }, [fetchC]);

  const startResearch = useCallback(async () => {
    setResearching(true);
    try {
      await api.post(`/agent/candidates/${id}/research`);
      poll.current && clearInterval(poll.current);
      poll.current = setInterval(async () => {
        const d = await fetchC();
        if (d && d.status === "researched" && d.research) {
          clearInterval(poll.current);
          setResearching(false);
          toast.success("Research completed — story moved to Researched.");
        }
      }, 4000);
    } catch { setResearching(false); toast.error("Research failed to start"); }
  }, [id, fetchC]);

  // auto-start when arriving with ?research=1 (guarded so it never fires twice)
  useEffect(() => {
    if (c && sp.get("research") === "1" && c.status === "new" && !researching && !autoStarted.current) {
      autoStarted.current = true;
      startResearch();
      sp.delete("research"); setSp(sp, { replace: true });
    }
  }, [c, sp, researching, startResearch, setSp]);

  const createDraft = async () => {
    const rec = (c.research || {}).recommended_action;
    const unver = (((c.research || {}).unverified_claims) || ((c.research || {}).caution) || []).length;
    const pol = draftPolicy(rec, unver);
    if (pol.mode === "blocked" && !overrideDNP) {
      toast.error("‘DO NOT PUBLISH’ — use ‘Override Editorial Warning’ first."); return;
    }
    if ((pol.mode === "warn" || pol.mode === "confirm") && !window.confirm(pol.msg)) return;
    setDrafting(true);
    try {
      await api.post(`/agent/candidates/${id}/draft`);
      toast.success("Draft creation started — it will appear under Awaiting Review.");
      poll.current && clearInterval(poll.current);
      poll.current = setInterval(async () => {
        const d = await fetchC();
        if (d && d.status === "drafted") { clearInterval(poll.current); setDrafting(false); toast.success("Draft created."); }
      }, 4000);
    } catch { setDrafting(false); toast.error("Draft failed to start"); }
  };
  const ignore = async () => { await api.post(`/agent/candidates/${id}/ignore`); toast.success("Moved to Ignored."); fetchC(); };
  const backToNew = async () => { await api.post(`/agent/candidates/${id}/reset`); toast.success("Moved back to New."); fetchC(); };

  if (!c) return <AdminLayout title="Candidate Story"><div className="text-admmuted">Loading…</div></AdminLayout>;

  const research = c.research || {};

  return (
    <AdminLayout title="Candidate Story">
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => nav("/admin/candidates")} className="flex items-center gap-1.5 text-admtext2 text-sm hover:text-admtext"><ArrowLeft size={15} /> Candidate Stories</button>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 ${STATUS_BADGE[c.status] || ""}`} data-testid="detail-status">{c.status}</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          {/* Header */}
          <div className="bg-admcard border border-admborder p-5">
            <div className="flex items-center gap-2 flex-wrap mb-2">
              <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border ${PRIORITY_COLOR[c.priority] || ""}`}>{c.priority}</span>
              <span className="text-[10px] uppercase tracking-wider text-admmuted">{c.category}</span>
              <span className="text-[10px] text-admmuted font-mono">· discovered {timeAgo(c.discovered_at)}</span>
            </div>
            <h1 className="font-serif text-2xl text-admtext leading-snug" data-testid="detail-headline">{c.headline}</h1>
          </div>

          {/* Discovery summary */}
          <Panel title="Discovery Summary">
            <p className="text-sm text-admtext2 leading-relaxed">{c.summary || "—"}</p>
          </Panel>

          {/* Research */}
          <div className="bg-admcard border border-admborder p-5" data-testid="detail-research">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-bold uppercase tracking-widest text-amber">Expanded Research</h3>
              {c.researched_at && <span className="text-[10px] text-admmuted font-mono">researched {timeAgo(c.researched_at)}</span>}
            </div>
            {researching ? (
              <div className="flex items-center gap-2 text-cyan text-sm" data-testid="research-in-progress">
                <span className="w-2 h-2 rounded-full bg-cyan animate-pulse-dot" /> Searching the live web for primary & independent sources… this can take up to a minute.
              </div>
            ) : c.status === "new" ? (
              <p className="text-admmuted text-sm">Not yet researched. Use “Research Further” to investigate this story across the live web.</p>
            ) : (research.expanded_summary || research.research_notes || (research.verified_facts || []).length) ? (
              <div className="space-y-4 text-sm">
                {!(research.recommended_action || research.queries || research.discovered_urls) && (
                  <div className="border border-amber/40 bg-amber/10 text-amber text-xs p-2" data-testid="legacy-research-banner">
                    Legacy research record. Click “Research Further Again” to run full live-web source discovery.
                  </div>
                )}
                {research.recommended_action && (
                  <div data-testid="recommended-action">
                    <span className="text-[10px] uppercase tracking-widest text-admmuted mr-2">Recommended editorial action</span>
                    <span className={`text-[11px] font-bold uppercase tracking-wider px-2 py-1 ${ACTION_COLOR[research.recommended_action] || "bg-admborder text-admtext2"}`}>{research.recommended_action}</span>
                  </div>
                )}
                {research.total_discovered != null && (
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <Stat n={research.total_discovered} label="Discovered" />
                    <Stat n={research.sources_read} label="Read" color="text-emerald" />
                    <Stat n={research.sources_inaccessible} label="Inaccessible" color="text-crimson" />
                  </div>
                )}
                {(research.expanded_summary || research.research_notes) && (
                  <div><div className="uppercase tracking-wider font-bold text-xs text-admtext2 mb-1">Expanded Research Summary</div>
                    <p className="text-admtext2 leading-relaxed">{research.expanded_summary || research.research_notes}</p></div>
                )}
                <FCList title="Verified Facts" items={research.verified_facts || research.verified_points} color="text-emerald" />
                <FCList title="Unverified Claims" items={research.unverified_claims || research.caution} color="text-amber" />
                <FCList title="Conflicting Information" items={research.conflicting_information} color="text-crimson" />
                {research.duplicate_note && (
                  <div><div className="uppercase tracking-wider font-bold text-xs text-cyan mb-1">Duplicate Check</div>
                    <p className="text-admtext2">{research.duplicate_note}</p></div>
                )}
                <SourceLinks title="Discovery Source" items={research.discovery_sources} />
                <SourceLinks title="Primary Sources" items={research.primary_sources} color="text-emerald" />
                <SourceLinks title="Secondary Sources" items={research.secondary_sources} />
                <SourceLinks title="Background Sources" items={research.background_sources} />
                {asArr(research.inaccessible_urls).length > 0 && (
                  <div><div className="uppercase tracking-wider font-bold text-xs text-crimson mb-1">Inaccessible ({asArr(research.inaccessible_urls).length})</div>
                    <div className="flex flex-col gap-1">{asArr(research.inaccessible_urls).map((u, i) => <span key={i} className="text-[11px] text-admmuted break-all line-through">{u}</span>)}</div></div>
                )}
                {asArr(research.queries).length > 0 && (
                  <div><div className="uppercase tracking-wider font-bold text-xs text-admmuted mb-1">Queries performed</div>
                    <div className="flex flex-col gap-0.5">{asArr(research.queries).map((q, i) => <span key={i} className="text-[11px] text-admtext2 font-mono">› {q}</span>)}</div></div>
                )}
              </div>
            ) : <p className="text-admmuted text-sm">Research recorded but no notes were parsed.</p>}
          </div>
        </div>

        {/* Sidebar meta + actions */}
        <div className="space-y-4">
          <div className="bg-admcard border border-admborder p-5 space-y-3 text-sm">
            <Meta label="Importance Score" value={<span className="font-mono text-2xl text-cyan">{c.importance_score}</span>} />
            <Meta label="Priority" value={c.priority} />
            <Meta label="Duplicate Risk" value={c.duplicate_risk} />
            <Meta label="Primary Source" value={c.primary_source || "—"} />
            <Meta label="Companies" value={asArr(c.companies).join(", ") || "—"} />
            <Meta label="Number of Sources" value={c.source_count ?? asArr(c.sources).length} />
            <div>
              <div className="text-admmuted uppercase tracking-wider text-[10px] mb-1">Source URLs</div>
              {asArr(c.sources).length ? (
                <div className="flex flex-col gap-1">
                  {asArr(c.sources).map((s, i) => (
                    <a key={i} href={typeof s === "string" ? s : s.url} target="_blank" rel="noreferrer" className="text-[11px] text-cyan hover:underline break-all flex items-center gap-1">
                      <span className="text-admmuted uppercase">[{(typeof s === "object" && s.type) || "src"}]</span> {typeof s === "string" ? s : s.url} <ExternalLink size={10} />
                    </a>
                  ))}
                </div>
              ) : <span className="text-admtext2 text-xs">—</span>}
            </div>
            {c.reasoning && (
              <div className="pt-2 border-t border-admborder">
                <div className="text-admmuted uppercase tracking-wider text-[10px] mb-1">Agent Reasoning</div>
                <p className="text-xs text-admtext2 border-l-2 border-cyan pl-2">{c.reasoning}</p>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="bg-admcard border border-admborder p-5 space-y-2">
            <h3 className="text-xs font-bold uppercase tracking-widest text-admtext2 mb-2">Actions</h3>
            {c.draft_id && (
              <Link to={`/admin/articles/${c.draft_id}`} className="flex items-center justify-center gap-1.5 bg-emerald text-admbg px-3 py-2 text-xs font-bold uppercase tracking-wider w-full">Open Draft →</Link>
            )}
            {(() => {
              const action = (c.research || {}).recommended_action;
              const meta = readinessMeta(action);
              const pol = draftPolicy(action, ((c.research || {}).unverified_claims || (c.research || {}).caution || []).length);
              const blocked = pol.mode === "blocked" && !overrideDNP;
              return (
                <>
                  {meta && (
                    <div className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 text-center border ${meta.color}`} data-testid="detail-readiness">{meta.label}</div>
                  )}
                  {blocked ? (
                    <button disabled data-testid="detail-create-draft-blocked"
                      className="flex items-center justify-center gap-1.5 border border-crimson/50 text-crimson px-3 py-2 text-xs uppercase tracking-wider w-full cursor-not-allowed">
                      <FileEdit size={13} /> Create Draft — Disabled</button>
                  ) : (
                    <button onClick={createDraft} disabled={drafting} data-testid="detail-create-draft"
                      className="flex items-center justify-center gap-1.5 bg-cyan text-admbg px-3 py-2 text-xs font-bold uppercase tracking-wider w-full hover:opacity-90 disabled:opacity-50">
                      <FileEdit size={13} /> {drafting ? "Creating draft…" : "Create Draft"}</button>
                  )}
                  {pol.mode === "blocked" && (
                    <button onClick={() => setOverrideDNP((v) => !v)} data-testid="detail-override-dnp"
                      className={`flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-wider w-full border ${overrideDNP ? "border-crimson text-crimson bg-crimson/10" : "border-admborder text-admmuted hover:border-crimson hover:text-crimson"}`}>
                      <ShieldAlert size={12} /> {overrideDNP ? "Override active — Create Draft enabled" : "Override Editorial Warning"}</button>
                  )}
                </>
              );
            })()}
            <button onClick={startResearch} disabled={researching} data-testid="detail-research-again"
              className="flex items-center justify-center gap-1.5 border border-admborder text-admtext px-3 py-2 text-xs uppercase tracking-wider w-full hover:border-cyan disabled:opacity-50">
              <Search size={13} /> {c.status === "new" ? "Research Further" : "Research Further Again"}</button>
            {c.status !== "new" && (
              <button onClick={backToNew} data-testid="detail-back-to-new"
                className="flex items-center justify-center gap-1.5 border border-admborder text-admtext2 px-3 py-2 text-xs uppercase tracking-wider w-full hover:border-cyan"><RotateCcw size={13} /> Back to New</button>
            )}
            <button onClick={ignore} data-testid="detail-ignore"
              className="flex items-center justify-center gap-1.5 border border-admborder text-admmuted px-3 py-2 text-xs uppercase tracking-wider w-full hover:border-crimson hover:text-crimson"><X size={13} /> Ignore</button>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}

function Panel({ title, children }) {
  return <div className="bg-admcard border border-admborder p-5"><h3 className="text-xs font-bold uppercase tracking-widest text-admtext2 mb-3">{title}</h3>{children}</div>;
}
function Meta({ label, value }) {
  return <div className="flex items-center justify-between gap-3"><span className="text-admmuted uppercase tracking-wider text-[10px]">{label}</span><span className="text-admtext text-right">{value}</span></div>;
}
function FCList({ title, items, color }) {
  const arr = asArr(items);
  if (!arr.length) return null;
  return <div><div className={`uppercase tracking-wider font-bold text-xs ${color} mb-1`}>{title}</div><ul className="space-y-1 text-admtext2 list-disc pl-4 text-sm">{arr.map((it, i) => <li key={i}>{typeof it === "string" ? it : JSON.stringify(it)}</li>)}</ul></div>;
}

const ACTION_COLOR = {
  "READY FOR DRAFT": "bg-emerald/15 text-emerald",
  "NEEDS MORE RESEARCH": "bg-cyan/15 text-cyan",
  "SINGLE-SOURCE — HUMAN REVIEW": "bg-amber/15 text-amber",
  "CONFLICTING SOURCES — HUMAN REVIEW": "bg-amber/15 text-amber",
  "DO NOT PUBLISH": "bg-crimson/15 text-crimson",
};

function Stat({ n, label, color = "text-admtext" }) {
  return <div className="border border-admborder py-2"><div className={`font-mono text-2xl ${color}`}>{n ?? 0}</div><div className="text-[10px] text-admmuted uppercase tracking-wider">{label}</div></div>;
}

function SourceLinks({ title, items, color = "text-admtext2" }) {
  const arr = asArr(items).filter((s) => s && (typeof s === "string" ? s : s.url));
  if (!arr.length) return null;
  return (
    <div>
      <div className={`uppercase tracking-wider font-bold text-xs ${color} mb-1`}>{title} ({arr.length})</div>
      <div className="flex flex-col gap-1">
        {arr.map((s, i) => (
          <a key={i} href={typeof s === "string" ? s : s.url} target="_blank" rel="noreferrer"
             className="text-[11px] text-cyan hover:underline break-all flex items-center gap-1">
            <ExternalLink size={10} className="shrink-0" /> {typeof s === "string" ? s : (s.title || s.url)}
          </a>
        ))}
      </div>
    </div>
  );
}
